import { useApi } from '@/services/queryUtils';
import {
  payoutEarningsFetcher,
  payoutCampaignsFetcher,
  payoutsFetcher,
  payoutShowsFetcher,
  payoutDatesV2Fetcher,
} from './fetchers';
import type {
  CampaignsByCategory,
  NetworkShow,
  PayoutDataV2,
  PayoutEarnings,
  Payouts,
} from '@/types';
import type { UseQueryOptions } from '@tanstack/react-query';

/**
 * Query hooks for payouts API.
 * @param networkId - The network ID.
 * @param year - The year.
 * @param options - The query options.
 * @returns A query hook for fetching payouts.
 */
export const usePayoutsQuery = (
  networkId?: string,
  year?: string,
  options?: Omit<UseQueryOptions<Payouts, Error>, 'queryKey' | 'queryFn'>
) => {
  const enabled = !!networkId && !!year;
  return useApi(['payouts', networkId, year], payoutsFetcher({ networkId, year }), {
    ...options,
    enabled,
  });
};

export const usePayoutCampaignsQuery = (
  networkId?: string,
  year?: string,
  month?: string,
  options?: Omit<UseQueryOptions<CampaignsByCategory, Error>, 'queryKey' | 'queryFn'>
) => {
  const enabled = !!networkId && !!year && !!month;
  return useApi(
    ['payout-campaigns', networkId, year, month],
    payoutCampaignsFetcher({ networkId, year, month }),
    { ...options, enabled }
  );
};

export const usePayoutDatesV2Query = (
  networkId?: string,
  options?: Omit<UseQueryOptions<PayoutDataV2, Error>, 'queryKey' | 'queryFn'>
) => {
  const enabled = !!networkId;
  return useApi(['payout-dates-v2', networkId], payoutDatesV2Fetcher({ networkId }), {
    ...options,
    enabled,
  });
};

export const usePayoutEarningsQuery = (
  networkId?: string,
  year?: string,
  month?: string,
  options?: Omit<UseQueryOptions<PayoutEarnings, Error>, 'queryKey' | 'queryFn'>
) => {
  const enabled = !!networkId && !!year && !!month;
  return useApi(
    ['payout-earnings', networkId, year, month],
    payoutEarningsFetcher({ networkId, year, month }),
    { ...options, enabled }
  );
};

export const usePayoutShowsQuery = (
  networkId?: string,
  year?: string,
  month?: string,
  options?: Omit<UseQueryOptions<NetworkShow[], Error>, 'queryKey' | 'queryFn'>
) => {
  const enabled = !!networkId && !!year && !!month;
  return useApi(
    ['payout-shows', networkId, year, month],
    payoutShowsFetcher({ networkId, year, month }),
    { ...options, enabled }
  );
};